import { type ClassValue, clsx } from 'clsx';
import { getCookie, setCookie } from 'cookies-next';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';
import { CustomNextImgLoaderProps, ParamsObject } from '@/frontastic/tastics/types';
import { TypePageTexts } from '@/types/i18n';
import { DisablePages } from '../../components/customercenter-ui/components/customer-detail/types';
import {
  COOKIE_KEYS,
  SITE_KEY_MAP,
  SITE_KEY_MAP_MO,
  DEFAULT_AGENT_NAME,
  ISO_DATE_TIME_FORMAT,
} from '../../constants/constant';
import { GlobalPageTexts } from '../i18n';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function objectToQueryString(params: ParamsObject): string {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    value && queryParams.append(key, String(value));
  });
  return queryParams.toString();
}

export function queryStringToObject(queryString: string): ParamsObject {
  const params = new URLSearchParams(queryString);
  const object: ParamsObject = {};
  params.forEach((value, key) => {
    if (object.hasOwnProperty(key)) {
      if (Array.isArray(object[key])) {
        object[key].push(value);
      } else {
        object[key] = [object[key], value];
      }
    } else {
      object[key] = value;
    }
  });

  return object;
}

export function formatMobileNumber(mobileNumber: string): string {
  // Remove any non-digit characters from the input string
  const cleanedNumber = mobileNumber.replace(/\D/g, '');

  // Check if the cleaned number has at least 10 digits
  if (cleanedNumber.length < 10) {
    return mobileNumber; // Return the original input if it's too short
  }

  // Split the cleaned number into area code, prefix, and line number
  const areaCode = cleanedNumber.slice(0, 3);
  const prefix = cleanedNumber.slice(3, 6);
  const lineNumber = cleanedNumber.slice(6);

  // Format the number according to the desired pattern
  const formattedNumber = `(${areaCode}) ${prefix}-${lineNumber}`;

  return formattedNumber;
}

export function formatAmount(amount: string) {
  // Convert the amount to a string and remove any non-numeric characters
  const numericValue = amount.toString().replace(/[^0-9.-]+/g, '');

  // Check if the amount is a valid number
  if (isNaN(parseFloat(numericValue))) {
    return '';
  }

  // Convert the amount to a float
  const floatValue = parseFloat(numericValue);

  // Format the amount with comma separators and add the dollar sign
  const formattedAmount =
    '$' +
    floatValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return formattedAmount;
}

export function getStatusLabel(status: any): string {
  const statusMap: Record<string, string> = {
    Processed: 'Processed',
    Open: 'Open',
    Voided: 'Voided',
    Void: 'Voided',
    processed: 'Processed',
    open: 'Open',
    voided: 'Voided',
    void: 'Voided',
    P: 'Processed',
    O: 'Open',
    V: 'Voided',
  };

  return statusMap[status] || 'Unknown';
}

export function formatDate(dateString: string) {
  // Check if the input is a valid date string in the format "yyyy-mm-dd"
  const isValidFormat = /^\d{4}-\d{2}-\d{2}$/.test(dateString);

  if (!isValidFormat) {
    return '';
  }

  // Split the date string into components
  const [year, month, day] = dateString.split('-');

  // Create a new Date object from the components
  const date = new Date(`${year}-${month}-${day}`);

  // Format the date using the desired format (dd/mm/yyyy)
  const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}/${date.getFullYear()}`;

  return formattedDate;
}

export function mapSiteIdToName(sites?: string[]) {
  if (!sites) {
    return [];
  }
  return sites.map((site) => ({
    name: SITE_KEY_MAP[site] ?? 'Unknown Site',
    value: site,
  }));
}

export function mapSiteIdToNameMO(sites?: string[]) {
  if (!sites) {
    return [];
  }
  return sites.map((site) => ({
    name: SITE_KEY_MAP_MO[site] ?? 'Unknown Site',
    value: site,
  }));
}

export const customNextImgLoader = ({ src }: CustomNextImgLoaderProps): string => {
  return `${src}`;
};

export function getCookieValue(key: string): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split('; ');
  const cookie = cookies.find((cookie) => cookie.startsWith(key + '='));
  return cookie?.split('=')[1] ? decodeURIComponent(cookie.split('=')[1]) : null;
}

export function getSelectedDivision() {
  const currentSite = getCookieValue(COOKIE_KEYS?.selectedSite);
  return currentSite?.slice(0, 2);
}

export function getCurrentEnvironment() {
  const currentEnvironment = getCookieValue(COOKIE_KEYS?.CURRENT_ENVIRONMENT);
  return currentEnvironment;
}

export function getSelectedSite() {
  const currentSite = getCookieValue(COOKIE_KEYS?.selectedSite);
  return currentSite;
}

export const formatDateMoment = (Timestamp: string, format: string) => {
  if (!Timestamp) return '';
  return moment.utc(Timestamp).format(format);
};

export const formatClaimResolvedOn = (date?: string): string => {
  if (!date) return '';
  const parsed = moment(date, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD'], true);
  return parsed.isValid() ? parsed.format('MM/DD/YY') : '';
};

export function formatCurrency(amt: string) {
  if (amt === '0') return '$0.00';
  if (!amt || amt === undefined) {
    return '';
  }
  const amount: any = parseFloat(amt);
  if (amount < 0) {
    return `-$${Math.abs(amount).toFixed(2)}`;
  }

  return `$${amount.toFixed(2)}`;
}

export const getSiteDivisionCode = (selectedSite: string): string => {
  return selectedSite?.substring(0, 2);
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  const decoded = decodeURIComponent(phoneNumber);
  const digits = decoded.replace(/\D+/g, '');
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phoneNumber; // Return original if it does not match expected pattern
};

export const getApiKeys = (settings: any, selectedSite: string) => {
  if (!settings || !selectedSite) return {};
  const currentSiteSettings = {} as any;

  Object.keys(settings).forEach((obj) => {
    if (obj.endsWith(selectedSite)) {
      currentSiteSettings[obj?.slice(0, -3)] = settings[obj];
    }
  });
  return currentSiteSettings;
};
export const getCurrentDateTime = () => {
  const currentDateTime = new Date();

  const year = currentDateTime.getFullYear();
  const month = String(currentDateTime.getMonth() + 1).padStart(2, '0');
  const day = String(currentDateTime.getDate()).padStart(2, '0');
  const hours = String(currentDateTime.getHours()).padStart(2, '0');
  const minutes = String(currentDateTime.getMinutes()).padStart(2, '0');
  const seconds = String(currentDateTime.getSeconds()).padStart(2, '0');

  const dateTimeString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;

  return dateTimeString;
};

export const getCurrentCentralTime = (): string => {
  return moment().tz('America/Chicago').format(ISO_DATE_TIME_FORMAT);
};

export const combinedKeyPressHandler = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
  if (
    !/[a-zA-Z0-9\s.,!?'"():;-]/i.test(event.key) &&
    event.key !== 'Backspace' &&
    event.key !== 'Tab' &&
    event.key !== 'ArrowLeft' &&
    event.key !== 'ArrowRight'
  ) {
    event.preventDefault();
  }
};

export const getPageUrl = (pathName: string) => {
  const historyUrls = getCookie(COOKIE_KEYS.URL_HISTORY);
  if (historyUrls) {
    const parsedUrl = JSON.parse(historyUrls);
    return parsedUrl[pathName]?.link ?? '';
  }
  return '';
};

export const PathNames = {
  PRODUCT: 'product',
  CUSTOMER_SEARCH: 'customer-search',
  CUSTOMER_DETAILS: 'customer-details',
  SEARCH: 'search',
  CART: 'cart',
  ORDER_SEARCH: 'order-search',
  ORDER_DETAILS: 'order-details',
};

export const extractCustomerId = (urlString: string) => {
  const regex = /[?&]customerId=([^&]*)/;
  const match = regex.exec(urlString);
  return match ? match[1] : null;
};
export const formatDateMomentCST = (Timestamp: string, format: string) => {
  if (!Timestamp) return '';
  return moment.utc(Timestamp).zone(-1140).format(format);
};

export const compareAddresses = (formData: any, pickListSelectedAddress: any) => {
  if (!pickListSelectedAddress) {
    return true;
  }
  const fieldsToCompare = ['streetName', 'state', 'postalCode', 'city'];
  if (formData.additionalAddressInfo || pickListSelectedAddress.additionalAddressInfo) {
    fieldsToCompare.push('additionalAddressInfo');
  }
  for (const field of fieldsToCompare) {
    if (formData[field] !== pickListSelectedAddress[field]) {
      return true;
    }
  }
  return false;
};

export const formatPhoneNumberForDisplay = (phoneNumber: string): string => {
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length !== 10) console.error('Invalid Phone Number');
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

export const DisableDropDown = (DisablePages: DisablePages[], currentPage: string) => {
  return DisablePages?.some((page) => page?.name === currentPage);
};

export const SetCookie = (cookiekey: string, cookieData: any) => {
  setCookie(cookiekey, cookieData, { sameSite: 'none', secure: true, partitioned: true });
};

export function extractAddressComponents(suggestionAddress: string, postalCode: string) {
  const components = {
    streetName: '',
    city: '',
    state: '',
    additionalAddressInfo: '',
    postalCode: '',
  };

  // Split the address string by comma
  const addressParts = suggestionAddress.split(',');

  // Extract street name and additional address
  const streetPart = addressParts[0].trim();
  const streetNameParts = streetPart.split(' ');
  components.streetName = streetNameParts.slice(0, -2).join(' ');
  components.additionalAddressInfo = streetNameParts.slice(-2).join(' ');

  // Extract city and state
  const cityState = addressParts[1].trim().split(' ');
  components.city = cityState.slice(0, -1).join(' ');
  components.state = cityState[cityState.length - 1];
  components.postalCode = postalCode;

  return components;
}

export function getAllowedPermissions(role: string, agentRolesConfig: string): string[] {
  const parsedAgentRolesConfig: { [section: string]: { PERMISSIONS: { [permission: string]: string[] } } } =
    agentRolesConfig ? JSON.parse(agentRolesConfig) : '';

  const allowedPermissionsForAgent: string[] = [];

  for (const permissions of Object.values(parsedAgentRolesConfig)) {
    for (const [permission, roles] of Object.entries(permissions.PERMISSIONS)) {
      if (roles.includes(role?.toUpperCase())) {
        allowedPermissionsForAgent.push(permission);
      }
    }
  }

  return allowedPermissionsForAgent;
}

export const getDivisionName = (divisonCode: string) => {
  switch (divisonCode) {
    case 'ZB':
      return 'Stoneberry';
    case 'ZY':
      return 'Masseys';
    case 'ZD':
      return 'Masoneasypay';
    case 'MQ':
      return 'Marylandsquare';
    case 'SM':
      return 'ShoeMall';
    case 'ZA':
      return 'K.Jordan';
    case 'ZC':
      return 'Fifth & Glam';
    case 'FB':
      return "Figi's Gallery";
    case 'FG':
      return "Figi's GIGT";
    case 'ZF':
      return 'Birchland Market';
    default:
      return 'Stoneberry';
  }
};

export const getFormattedSiteName = (divisonCode: string) => {
  switch (divisonCode) {
    case 'ZB':
      return 'Stoneberry';
    case 'ZY':
      return 'Masseys';
    case 'ZD':
      return 'Mason Outfitters';
    case 'MQ':
      return 'Maryland Square';
    case 'SM':
      return 'Shoemall';
    case 'ZA':
      return 'K.Jordan';
    case 'ZC':
      return 'Fifth & Glam';
    case 'FB':
      return "Figi's Gallery";
    case 'FG':
      return "Figi's GIGT";
    case 'ZF':
      return 'Birchland Market';
    default:
      return 'Stoneberry';
  }
};

export const getFormattedBrandSiteName = (divisonCode: string) => {
  switch (divisonCode) {
    case 'ZB':
      return 'Stoneberry';
    case 'ZY':
      return 'Masseys';
    case 'ZD':
      return 'Mason Easy-Pay';
    case 'MQ':
      return 'Maryland Square';
    case 'SM':
      return 'Shoemall';
    case 'ZA':
      return 'K.Jordan';
    case 'ZC':
      return 'Fifth & Glam';
    case 'FB':
      return "Figi's Gallery";
    case 'FG':
      return "Figi's GIGT";
    case 'ZF':
      return 'Birchland Market';
    default:
      return 'Stoneberry';
  }
};

export const getTimeStamp = (timeZone?: string) => {
  const options = {
    timeZone: timeZone ?? 'America/Chicago', // by default CST
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false, // Use 24-hour time format
  } as any;

  const formatter = new Intl.DateTimeFormat('en-US', options);

  // Format date and time
  const parts = formatter.formatToParts(new Date());
  const year = parts?.find((part) => part?.type === 'year')?.value;
  const month = parts?.find((part) => part?.type === 'month')?.value;
  const day = parts?.find((part) => part?.type === 'day')?.value;
  const hour = parts?.find((part) => part?.type === 'hour')?.value;
  const minute = parts?.find((part) => part?.type === 'minute')?.value;

  // Format into ISO-like timestamp
  return `${year}-${month}-${day}T${hour}:${minute}:00`;
};

export const getAgentName = () => {
  let agentEmail = getCookie(COOKIE_KEYS.AGENT_EMAIL);
  if (agentEmail === 'undefined' || agentEmail === 'null' || !agentEmail) {
    agentEmail = DEFAULT_AGENT_NAME;
  }
  return agentEmail;
};

export const isCreditSite = () => {
  const currentSite = getSelectedDivision();
  switch (currentSite) {
    case 'ZB':
      return true;
    case 'ZY':
      return true;
    case 'ZD':
      return true;
    case 'ZC':
      return true;
    case 'ZA':
      return true;
    case 'FG':
      return true;
    case 'FB':
      return true;
    case 'ZF':
      return true;
    default:
      return false;
  }
};

export const getCustomerEmail = () => {
  return sessionStorage.getItem('customerEmail');
};

export const getCurrentDateTimeCST = () => {
  const now = new Date().toISOString();
  const currentDateTime = moment.utc(now).tz('America/Chicago').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

  if (!currentDateTime) return '';

  const [datePart, timePart] = currentDateTime.split('T');
  if (!datePart || !timePart) return currentDateTime;

  const [year, month, day] = datePart.split('-');
  const [hours, minutes, seconds] = timePart.split(':');

  const dateTimeString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;

  return dateTimeString;
};

export const TranslateText = (PageTexts: TypePageTexts, siteLang: string): { [key: string]: string } => {
  return { ...PageTexts[siteLang], ...GlobalPageTexts[siteLang] };
};

export const createUrl = (
  customerId: string,
  omsCustomerId: string,
  divisionCode: string,
  creditAccountId?: string,
  mailingRecordId?: string,
) => {
  return `?customerId=${customerId ?? ''}&omsCustomerId=${omsCustomerId ?? ''}&creditAccountId=${
    creditAccountId ?? ''
  }&durationInMonths=24&divisionCode=${divisionCode ?? ''}&mailingRecordId=${mailingRecordId ?? ''}&idName=customerId`;
};

export const getBreadCrumb = (page: string) => {
  switch (page) {
    case 'cart':
      return 'Shopping Cart';
    case 'order-details':
      return 'Order Details';
    case 'checkout':
      return 'Check Out';
    default:
      return '';
  }
};

export const OrderDetailsUrl = (
  orderId: string,
  divisionCode?: string,
  isExternalOrderId?: string,
  requestOrderLock?: string,
  orderZipCode?: string,
  fromCustomerSearch = false,
) => {
  const timestamp = new Date().toISOString();
  return `/order-details/?orderId=${orderId ?? ''}&divisionCode=${divisionCode ?? ''}&isExternalOrderId=${
    isExternalOrderId ?? ''
  }&requestOrderLock=${requestOrderLock ?? ''}&orderZipCode=${
    orderZipCode ?? ''
  }&timestamp=${timestamp}&fromCustomerSearch=${fromCustomerSearch}`;
};

export const getDivisionCode = (division: string) => {
  switch (division) {
    case 'Masseys':
      return 'ZY';
    case 'Stoneberry':
      return 'ZB';
    case 'ShoeMall':
      return 'SM';
    case 'Maryland Square':
      return 'MQ';
    case 'Mason Easy Pay':
      return 'ZD';
    case 'Mason Easy-Pay':
      return 'ZD';
    case 'Birchland Market':
      return 'ZF';
    case 'K. Jordan':
      return 'ZA';
    case 'Fifth & Glam':
      return 'ZC';
    case "Figi's GIGT":
      return 'FG';
    case "Figi's Gallery":
      return 'FB';
    case 'Mason Outfitters':
      return 'ZD';
    default:
      return 'ZB';
  }
};

const DOMAIN_MAP: Record<string, string> = {
  ZB: 'stoneberry.com',
  ZY: 'masseys.com',
  ZD: 'masonoutfitters.com',
  MQ: 'marylandsquare.com',
  SM: 'shoemall.com',
};

const ENV_PREFIX_MAP: Record<string, string> = {
  Production: 'https://www.',
  Staging: 'https://www-stg2.',
  Development: 'https://www-dev2.',
};

export const getFAQLink = (url: string) => {
  const divisionCode = getSelectedDivision() ?? 'ZB';
  const environment = getCurrentEnvironment() ?? 'Production';

  const prefix = ENV_PREFIX_MAP[environment] ?? ENV_PREFIX_MAP['Production'];
  const domain = DOMAIN_MAP[divisionCode] ?? DOMAIN_MAP['ZB'];

  return `${prefix}${domain}${url}`;
};

export const getSiteBaseLink = (url: string) => {
  const divisionCode = getSelectedDivision() ?? 'ZB';
  const environment = getCurrentEnvironment() ?? 'Production';

  const prefix = ENV_PREFIX_MAP[environment] ?? ENV_PREFIX_MAP['Production'];
  const domain = DOMAIN_MAP[divisionCode] ?? DOMAIN_MAP['ZB'];

  return `${prefix}${domain}${url}`;
};
